<template>
  <div class="portfolio-wrapper" :class="{ 'carteras-tab': activeTab === 'carteras' }">
    <PageHeader 
      title="Gestor de Carteras" 
      titleClass="portfolio-title"
      subtitleClass="portfolio-text">
      Gestiona y supervisa todas tus carteras de inversión desde un solo lugar. 
      Controla posiciones, valores en espera, histórico y balance de manera eficiente.
    </PageHeader>

    <!-- Beta Banner -->
    <div class="beta-banner">
      <div class="beta-banner-content">
        <span class="beta-badge">NUEVO</span>
        <span class="beta-text">
          Esta es la nueva versión de gestión de carteras que estamos probando. Si prefieres usar la versión anterior que ya conoces, puedes acceder 
          <a href="https://www.eurekers.com/carteras" target="_blank" rel="noopener noreferrer" class="beta-link">
            haciendo clic aquí
            <span class="material-icons-outlined">open_in_new</span>
          </a>
          <br>
          ¿Has encontrado algún error o tienes sugerencias? Compártelo en 
          <a href="https://www.eurekers.com/app/forum/thread/265863" target="_blank" rel="noopener noreferrer" class="beta-link">
            nuestro foro
            <span class="material-icons-outlined">open_in_new</span>
          </a>
        </span>
      </div>
    </div>

    <div class="portfolio-manager-gcn">
      <!-- Selected Portfolio Name Display -->
      <div v-if="selectedPortfolioId && selectedPortfolioName" class="selected-portfolio-name">
        <span class="portfolio-label">Cartera seleccionada:</span>
        <span class="portfolio-name">{{ selectedPortfolioName }}</span>
      </div>

      <!-- Portfolio Summary Header -->
      <div v-if="selectedPortfolioId" class="portfolio-summary-header">
          <!-- Summary Header with Toggle -->
          <div class="summary-header-toggle" @click="portfolioSummaryExpanded = !portfolioSummaryExpanded">
            <div class="summary-title">
              <span class="material-icons-outlined">analytics</span>
              <span>Resumen de Cartera</span>
              <span class="summary-badge" v-if="!portfolioDetailsLoading && portfolioDetails">
                {{ waitingPositions.length + openPositions.length }} en cartera ({{ waitingPositions.length }} en espera, {{ openPositions.length }} abiertas)
              </span>
            </div>
            <span class="material-icons-outlined toggle-icon" :class="{ 'expanded': portfolioSummaryExpanded }">
              expand_more
            </span>
          </div>

          <!-- Collapsible Content -->
          <div v-if="portfolioSummaryExpanded" class="summary-content">
              <div v-if="portfolioDetailsLoading" class="loading-indicator-gcn small">Cargando resumen de cartera...</div>
              <div v-if="portfolioDetailsError" class="error-message-gcn small">{{ portfolioDetailsError }}</div>
              
              <div v-if="!portfolioDetailsLoading && portfolioDetails" class="portfolio-metrics-container">
            <!-- Virtual Portfolio Metrics (Waiting Positions) -->
            <div class="metrics-section virtual-metrics">
              <h4 class="section-title">
                <span class="material-icons-outlined">schedule</span>
                Métricas Virtuales (Valores en Espera)
              </h4>
              <ul class="portfolio-metrics">
                <li>
                    <span class="material-icons-outlined">trending_up</span>
                    Riesgo virtual alcista: <b>{{ formatPercentage(virtualUpRisk) }}</b>
                    <span class="separator">|</span>
                    <span class="material-icons-outlined">trending_down</span>
                    Riesgo virtual bajista: <b>{{ formatPercentage(virtualDownRisk) }}</b>
                </li>
                <li>
                    <span class="material-icons-outlined">insights</span>
                    Beneficio virtual total: <b>{{ formatPercentage(virtualTotalBenefit) }}</b>
                    (<b>{{ formatCurrencyRight(virtualTotalBenefitAmount, displayPortfolioCurrency) }}</b>)
                </li>
                <!-- <li>
                    <span class="material-icons-outlined">trending_up</span>
                    Beneficio virtual alcista: <b>{{ formatPercentage(virtualUpBenefit) }}</b>
                    (<b>{{ formatCurrencyRight(virtualUpBenefitAmount, displayPortfolioCurrency) }}</b>)
                    <span class="separator">|</span>
                    <span class="material-icons-outlined">trending_down</span>
                    Beneficio virtual bajista: <b>{{ formatPercentage(virtualDownBenefit) }}</b>
                    (<b>{{ formatCurrencyRight(virtualDownBenefitAmount, displayPortfolioCurrency) }}</b>)
                </li> -->
              </ul>
            </div>

            <!-- Real Portfolio Metrics (Open Positions) -->
            <div class="metrics-section real-metrics">
              <h4 class="section-title">
                <span class="material-icons-outlined">trending_up</span>
                Métricas Reales (Posiciones Abiertas)
              </h4>
              <ul class="portfolio-metrics">
                <li>
                    <span class="material-icons-outlined">trending_up</span>
                    Riesgo real alcista: <b>{{ formatPercentage(realUpRisk) }}</b>
                    <span class="separator">|</span>
                    <span class="material-icons-outlined">trending_down</span>
                    Riesgo real bajista: <b>{{ formatPercentage(realDownRisk) }}</b>
                </li>
                <li>
                    <span class="material-icons-outlined">account_balance</span>
                    Beneficio total: <b>{{ formatPercentage(portfolioOpenPositionsSummary.benefit) }}</b>
                    (<b>{{ formatCurrencyRight(portfolioOpenPositionsSummary.benefit_amount, displayPortfolioCurrency) }}</b>)
                </li>
                <li>
                    <span class="material-icons-outlined">security</span>
                    Beneficio asegurado total: <b>{{ formatPercentage(portfolioOpenPositionsSummary.insured_benefit) }}</b>
                    (<b>{{ formatCurrencyRight(portfolioOpenPositionsSummary.insured_benefit_amount, displayPortfolioCurrency) }}</b>)
                </li>
              </ul>
            </div>

            <!-- Portfolio Summary -->
            <div class="metrics-section summary-metrics">
              <h4 class="section-title">
                <span class="material-icons-outlined">account_balance_wallet</span>
                Resumen de Cartera
              </h4>
              <ul class="portfolio-metrics">
                <li>
                    <span class="material-icons-outlined">account_balance_wallet</span>
                    Montante total (pos. abiertas): <b>{{ formatCurrencyRight(portfolioOpenPositionsSummary.total_amount, displayPortfolioCurrency) }}</b>
                </li>
              </ul>
            </div>
           </div>
            </div>
      </div>

      <!-- Desktop tabsGcn (hidden on mobile) -->
      <div class="tabsGcn desktop-tabsGcn">
        <button
          v-for="tab in tabsGcn"
          :key="tab.id"
          class="tab-button-gcn"
          :class="{ active: activeTab === tab.id }"
          @click="changeTab(tab.id)"
          :disabled="isDisabled(tab)"
          :title="getTabTitle(tab)"
        >
          <span class="tab-label">{{ tab.name }}</span>
        </button>
      </div>

      <!-- Mobile select dropdown (hidden on desktop) -->
      <div class="mobile-tabsGcn">
        <label for="tab-select">Ir a sección:</label>
        <select 
          id="tab-select"
          v-model="activeTab"
          class="tab-select"
          @change="handleSelectChange"
          aria-label="Seleccionar pestaña"
        >
          <option 
            v-for="tab in tabsGcn" 
            :key="tab.id" 
            :value="tab.id"
            :disabled="isDisabled(tab)"
          >
            {{ tab.name }}
          </option>
        </select>
      </div>

      <div class="tab-content-gcn">
        <!-- Using conditional rendering with transitions -->
        <transition name="tab-fade" mode="out-in">
          <CarterasList v-if="activeTab === 'carteras'" 
            :key="'carteras'"
            :selected-portfolio-id="selectedPortfolioId"
            @portfolio-selected="handlePortfolioSelected"
          />
          
          <ValoresEnEspera v-else-if="activeTab === 'valoresEspera'" 
            :key="'vespera-' + selectedPortfolioId" 
            :portfolio-id="selectedPortfolioId"
          />
          
          <PosicionesAbiertas v-else-if="activeTab === 'posicionesAbiertas'" 
            :key="'pabiertas-' + selectedPortfolioId" 
            :portfolio-id="selectedPortfolioId"
          />
          
          <General v-else-if="activeTab === 'general'" 
            :key="'general-' + selectedPortfolioId" 
            :portfolio-id="selectedPortfolioId"
            :portfolio-name="selectedPortfolioName"
          />
          
          <Historico v-else-if="activeTab === 'historico'" 
            :key="'historico-' + selectedPortfolioId" 
            :portfolio-id="selectedPortfolioId"
          />
          
          <Balance v-else-if="activeTab === 'balance'" 
            :key="'balance-' + selectedPortfolioId" 
            :portfolio-id="selectedPortfolioId"
          />
        </transition>
      </div>
    </div>
  </div>
</template>

<script>
import CarterasList from './CarterasList.vue';
import ValoresEnEspera from './ValoresEnEspera.vue';
import PosicionesAbiertas from './PosicionesAbiertas.vue';
import General from './General.vue';
import Historico from './Historico.vue'; // Import
import Balance from './Balance.vue';   // Import
import apiService, { formatCurrency } from './apiService'; // Needed to fetch portfolio name for General tab
import './styles.css'; // Import global CSS
import PageHeader from '@/components/common/PageHeader.vue'; // Import PageHeader component

// Define implemented status outside data for easier management
const IMPLEMENTED_tabsGcn = ['carteras', 'valoresEspera', 'posicionesAbiertas', 'general', 'historico', 'balance']; // All tabsGcn
// Define tabsGcn requiring portfolio selection
const REQUIRES_SELECTION = ['valoresEspera', 'posicionesAbiertas', 'general', 'historico', 'balance'];

export default {
  name: 'PortfolioManager',
  components: {
    CarterasList,
    ValoresEnEspera,
    PosicionesAbiertas,
    General,
    Historico, // Register
    Balance,   // Register
    PageHeader, // Register PageHeader component
  },
  data() {
    return {
      activeTab: null,
      selectedPortfolioId: null,
      selectedPortfolioName: '', // Store name for General tab title
      tabsGcn: [
        { id: 'carteras', name: 'Carteras' },
        { id: 'valoresEspera', name: 'Valores en Espera' },
        { id: 'posicionesAbiertas', name: 'Posiciones Abiertas' },
        { id: 'general', name: 'General' },
        { id: 'historico', name: 'Histórico' },
        { id: 'balance', name: 'Balance' },
      ],
      // Portfolio summary data
      portfolioDetails: null,
      portfolioDetailsLoading: false,
      portfolioDetailsError: null,
      waitingPositions: [],
      openPositions: [],
      waitingPositionsLoading: false,
      openPositionsLoading: false,
      // Portfolio summary dropdown state
      portfolioSummaryExpanded: true,
    };
  },
  computed: {
    virtualUpRisk() {
      if (!this.waitingPositions || this.waitingPositions.length === 0) return 0;
      return this.waitingPositions.reduce((sum, pos) => {
        if (pos.direction === 1 && typeof pos.risk === 'number') { // Bullish
          return sum + pos.risk;
        }
        return sum;
      }, 0);
    },
    virtualDownRisk() {
      if (!this.waitingPositions || this.waitingPositions.length === 0) return 0;
      return this.waitingPositions.reduce((sum, pos) => {
        if (pos.direction !== 1 && typeof pos.risk === 'number') { // Bearish
          return sum + pos.risk;
        }
        return sum;
      }, 0);
    },
    virtualUpBenefit() {
      if (!this.waitingPositions || this.waitingPositions.length === 0) return 0;
      return this.waitingPositions.reduce((sum, pos) => {
        if (pos.direction === 1 && typeof pos.tthh_profit === 'number') { // Bullish
          return sum + pos.tthh_profit;
        }
        return sum;
      }, 0);
    },
    virtualDownBenefit() {
      if (!this.waitingPositions || this.waitingPositions.length === 0) return 0;
      return this.waitingPositions.reduce((sum, pos) => {
        if (pos.direction !== 1 && typeof pos.tthh_profit === 'number') { // Bearish
          return sum + pos.tthh_profit;
        }
        return sum;
      }, 0);
    },
    virtualTotalBenefit() {
      return this.virtualUpBenefit + this.virtualDownBenefit;
    },
    virtualUpBenefitAmount() {
      if (!this.waitingPositions || this.waitingPositions.length === 0) return 0;
      return this.waitingPositions.reduce((sum, pos) => {
        if (pos.direction === 1 && typeof pos.tthh_profit === 'number' && typeof pos.in_price === 'number' && typeof pos.instrument_number === 'number') {
          const investmentAmount = pos.in_price * pos.instrument_number;
          const benefitAmount = (investmentAmount * pos.tthh_profit) / 100;
          return sum + benefitAmount;
        }
        return sum;
      }, 0);
    },
    virtualDownBenefitAmount() {
      if (!this.waitingPositions || this.waitingPositions.length === 0) return 0;
      return this.waitingPositions.reduce((sum, pos) => {
        if (pos.direction !== 1 && typeof pos.tthh_profit === 'number' && typeof pos.in_price === 'number' && typeof pos.instrument_number === 'number') {
          const investmentAmount = pos.in_price * pos.instrument_number;
          const benefitAmount = (investmentAmount * pos.tthh_profit) / 100;
          return sum + benefitAmount;
        }
        return sum;
      }, 0);
    },
    virtualTotalBenefitAmount() {
      return this.virtualUpBenefitAmount + this.virtualDownBenefitAmount;
    },
    realUpRisk() {
      if (!this.openPositions || this.openPositions.length === 0) return 0;
      return this.openPositions.reduce((sum, pos) => {
        if (pos.direction === 1 && typeof pos.risk === 'number') { // Bullish
          return sum + pos.risk;
        }
        return sum;
      }, 0);
    },
    realDownRisk() {
      if (!this.openPositions || this.openPositions.length === 0) return 0;
      return this.openPositions.reduce((sum, pos) => {
        if (pos.direction !== 1 && typeof pos.risk === 'number') { // Bearish
          return sum + pos.risk;
        }
        return sum;
      }, 0);
    },
    portfolioOpenPositionsSummary() {
      return this.portfolioDetails && this.portfolioDetails.open_positions
             ? this.portfolioDetails.open_positions
             : { benefit: 0, benefit_amount: 0, insured_benefit: 0, insured_benefit_amount: 0, total_amount: 0 };
    },
    displayPortfolioCurrency() {
      return (this.portfolioDetails && this.portfolioDetails.currency) ? this.portfolioDetails.currency : 'EUR';
    },
  },
  watch: {
    activeTab() {
      this.updateUrlQueryParameters();
    },
    selectedPortfolioId() {
      this.updateUrlQueryParameters();
      if (!this.selectedPortfolioId && this.requiresSelection(this.activeTab)) {
        this.activeTab = 'carteras';
      }
    },
  },
  async mounted() {
    const params = new URLSearchParams(window.location.search);
    const tabFromUrlParam = params.get('tab');
    const portfolioIdFromUrlParam = params.get('portfolio');

    let finalSelectedPortfolioId = null;
    let finalSelectedPortfolioName = '';
    let portfolioActuallyLoaded = false;

    if (portfolioIdFromUrlParam) {
        try {
            const details = await apiService.getPortfolio(portfolioIdFromUrlParam);
            finalSelectedPortfolioName = details?.name || 'Seleccionada';
            finalSelectedPortfolioId = portfolioIdFromUrlParam;
            portfolioActuallyLoaded = true;
        } catch (e) {
            console.error("Mounted: Failed to fetch portfolio details from URL parameter:", e);
            finalSelectedPortfolioName = 'Detalles no disponibles';
            // finalSelectedPortfolioId remains null
            portfolioActuallyLoaded = false;
        }
    }

    let finalActiveTab = 'carteras'; // Default to 'carteras'

    if (tabFromUrlParam && this.tabsGcn.some(t => t.id === tabFromUrlParam) && this.isImplemented(tabFromUrlParam)) {
        // Valid & implemented tab in URL
        if (this.requiresSelection(tabFromUrlParam)) {
            if (portfolioActuallyLoaded) {
                finalActiveTab = tabFromUrlParam;
                // finalSelectedPortfolioId and Name are already set from above
            } else {
                // Requires portfolio, but not loaded/invalid. Default to carteras.
                finalActiveTab = 'carteras';
                finalSelectedPortfolioId = null; // Ensure portfolio is cleared
                finalSelectedPortfolioName = '';
            }
        } else {
            // Tab does not require selection, use it.
            finalActiveTab = tabFromUrlParam;
        }
    } else if (tabFromUrlParam) { 
        // Tab from URL is invalid or not implemented. Default to carteras.
        finalActiveTab = 'carteras';
        finalSelectedPortfolioId = null; // Clear portfolio
        finalSelectedPortfolioName = '';
    }
    // If no tabFromUrlParam, finalActiveTab remains 'carteras' (our default)

    // If the final decision is 'carteras', ensure no portfolio is selected at this stage
    if (finalActiveTab === 'carteras') {
        finalSelectedPortfolioId = null;
        finalSelectedPortfolioName = '';
    }
    
    // Now, set the component's data properties.
    this.selectedPortfolioId = finalSelectedPortfolioId;
    this.selectedPortfolioName = finalSelectedPortfolioName;
    this.activeTab = finalActiveTab; // This assignment will trigger watchers if value changes

    // Fetch portfolio summary data if a portfolio was loaded
    if (finalSelectedPortfolioId) {
      this.fetchPortfolioSummaryData();
    }

    this.updateUrlQueryParameters(); // Reflects the state after decisions
    window.addEventListener('popstate', this.handlePopState);
  },
  beforeDestroy() {
    window.removeEventListener('popstate', this.handlePopState);
  },
  methods: {
     isImplemented(tabId) { return IMPLEMENTED_tabsGcn.includes(tabId); },
     requiresSelection(tabId) { return REQUIRES_SELECTION.includes(tabId); },
     isDisabled(tab) { return !this.isImplemented(tab.id) || (this.requiresSelection(tab.id) && !this.selectedPortfolioId); },
     getTabTitle(tab) { if (!this.isImplemented(tab.id)) { return 'Próximamente'; } if (this.requiresSelection(tab.id) && !this.selectedPortfolioId) { return 'Selecciona una cartera primero'; } return ''; },
     changeTab(tabId) { const tab = this.tabsGcn.find(t => t.id === tabId); if (this.isDisabled(tab)) { return; } this.activeTab = tabId; },
     handleSelectChange(event) {
         const tabId = event.target.value;
         const tab = this.tabsGcn.find(t => t.id === tabId);
         if (!this.isDisabled(tab)) {
             this.activeTab = tabId;
         } else {
             // If disabled option somehow selected, reset to previous valid tab
             event.target.value = this.activeTab; // Visually reset the select element
         }
     },
     async fetchPortfolioName(portfolioId) {
        // This helper is primarily for setting the name.
        // It assumes the decision to use portfolioId has been made by the caller.
        if (!portfolioId) {
            this.selectedPortfolioName = '';
            return;
        }
        try {
            const details = await apiService.getPortfolio(portfolioId);
            this.selectedPortfolioName = details?.name || 'Seleccionada';
        } catch (e) {
            console.error("Failed to fetch portfolio name:", e);
            this.selectedPortfolioName = 'Detalles no disponibles';
            // It's crucial that this method does NOT clear selectedPortfolioId itself.
            // The caller that decided to use portfolioId should handle its invalidation.
        }
    },
     async handlePortfolioSelected(portfolioId) {
         console.log("Portfolio selected event with ID:", portfolioId);
         if (portfolioId) {
            try {
                // Validate and fetch details. This is the authoritative step for selection.
                const details = await apiService.getPortfolio(portfolioId);
                this.selectedPortfolioName = details?.name || 'Seleccionada';
                this.selectedPortfolioId = portfolioId; // Set ID only after successful fetch
                this.fetchPortfolioSummaryData(); // Fetch portfolio summary data
            } catch (e) {
                console.error("Failed to fetch selected portfolio details:", e);
                this.selectedPortfolioName = 'Detalles no disponibles';
                this.selectedPortfolioId = null; // Clear ID if fetch fails
            }
         } else {
              // Portfolio deselected by user
              this.selectedPortfolioId = null;
              this.selectedPortfolioName = '';
         }

         // If portfolio is now null (either deselected or failed fetch)
         // and current tab requires a selection, navigate to 'carteras'.
         if (!this.selectedPortfolioId && this.requiresSelection(this.activeTab)) {
            this.activeTab = 'carteras';
         }
         // Watchers for selectedPortfolioId and activeTab will call updateUrlQueryParameters.
    },
    // --- Portfolio Data Fetching Methods ---
    async fetchPortfolioSummaryData() {
      if (!this.selectedPortfolioId) return;
      
      this.portfolioDetailsLoading = true;
      this.portfolioDetailsError = null;
      
      try {
        // Fetch portfolio details
        const portfolioDetails = await apiService.getPortfolio(this.selectedPortfolioId);
        this.portfolioDetails = portfolioDetails;
        
        // Fetch waiting positions for virtual risk calculation
        const waitingPositions = await apiService.listWaitingPositions(this.selectedPortfolioId);
        this.waitingPositions = waitingPositions;
        
        // Fetch open positions for real risk calculation
        const openPositions = await apiService.listOpenPositions(this.selectedPortfolioId);
        this.openPositions = openPositions;
        
      } catch (err) {
        this.portfolioDetailsError = `Error cargando datos de cartera: ${err.message}`;
        console.error("Error fetching portfolio summary data:", err);
      } finally {
        this.portfolioDetailsLoading = false;
      }
    },
    
    // --- Formatting Methods ---
    formatNumberAnglo(value, decimals = 2) {
      if (typeof value !== 'number') return '-';
      const absValue = Math.abs(value);
      const parts = absValue.toFixed(decimals).split('.');
      const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      const decimalPart = parts[1];
      return decimalPart ? `${integerPart}.${decimalPart}` : integerPart;
    },
    formatPercentage(value) {
      if (typeof value !== 'number') return 'N/A';
      return `${this.formatNumberAnglo(value)}%`;
    },
    formatCurrency,
    formatCurrencyRight(value, currency = 'EUR') {
      if (typeof value !== 'number') return '-';
      const isNegative = value < 0;
      const formattedNumber = this.formatNumberAnglo(Math.abs(value));
      const currencySymbol = currency === 'EUR' ? '€' : currency;
      
      return isNegative ? `-${formattedNumber}${currencySymbol}` : `${formattedNumber}${currencySymbol}`;
    },
    
    // Portfolio Summary Methods
    togglePortfolioSummary() {
      this.portfolioSummaryExpanded = !this.portfolioSummaryExpanded;
    },
    
    updateUrlQueryParameters() {
      // Add guard to prevent execution if activeTab is not yet determined
      if (this.activeTab === null) {
          return;
      }
      const params = new URLSearchParams();
      
      // Logic for cleaner URLs:
      // - If tab is 'carteras' and no portfolio, no params needed (clean URL).
      // - Otherwise, set 'tab'.
      // - Always set 'portfolio' if it exists.
      if (this.activeTab !== 'carteras' || this.selectedPortfolioId) {
        params.set('tab', this.activeTab);
      }

      if (this.selectedPortfolioId) {
        params.set('portfolio', this.selectedPortfolioId);
      }

      const newQueryString = params.toString();
      const currentQueryString = window.location.search ? window.location.search.substring(1) : ""; // Remove leading '?'

      if (newQueryString !== currentQueryString) {
        const newUrl = window.location.pathname + (newQueryString ? `?${newQueryString}` : '');
        history.replaceState({ path: newUrl }, '', newUrl);
      }
    },
    async handlePopState() {
      const params = new URLSearchParams(window.location.search);
      const tabFromUrl = params.get('tab') || 'carteras'; // Default to 'carteras'
      const portfolioIdFromUrl = params.get('portfolio');

      let newSelectedPortfolioId = null;
      let newPortfolioName = '';
      let popStatePortfolioIsValid = false;

      if (portfolioIdFromUrl) {
        try {
          const details = await apiService.getPortfolio(portfolioIdFromUrl);
          newPortfolioName = details?.name || 'Seleccionada';
          newSelectedPortfolioId = portfolioIdFromUrl;
          popStatePortfolioIsValid = true;
        } catch (e) {
          console.error("PopState: Failed to fetch portfolio details from URL:", e);
          newPortfolioName = 'Detalles no disponibles';
          newSelectedPortfolioId = null; // Treat as invalid if fetch fails
          popStatePortfolioIsValid = false;
        }
      }
      
      // Update component state only if it has actually changed
      if (this.selectedPortfolioId !== newSelectedPortfolioId) {
          this.selectedPortfolioId = newSelectedPortfolioId;
          // Fetch portfolio summary data if portfolio changed
          if (newSelectedPortfolioId) {
            this.fetchPortfolioSummaryData();
          }
      }
      // Always update name, as it might change even if ID remains (e.g. from '' to 'Detalles no disponibles')
      this.selectedPortfolioName = newPortfolioName;


      let targetTab = 'carteras'; // Default/fallback
      if (this.tabsGcn.some(t => t.id === tabFromUrl) && this.isImplemented(tabFromUrl)) {
        if (this.requiresSelection(tabFromUrl) && !popStatePortfolioIsValid) {
          targetTab = 'carteras';
        } else {
          targetTab = tabFromUrl;
        }
      } else {
        targetTab = 'carteras'; // Fallback for invalid/unimplemented tab from URL
      }

      if (this.activeTab !== targetTab) {
        this.activeTab = targetTab;
      }
      // Watchers on activeTab and selectedPortfolioId will be triggered if values changed.
      // They will call updateUrlQueryParameters, which checks if the URL *actually*
      // needs to be updated, preventing an infinite loop with popstate.
    }
  },
};
</script>

<style scoped>
/* Main wrapper container to match Risk.vue styling */
.portfolio-wrapper {
  background-color: var(--color-background);
  color: var(--color-text-primary);
  padding: 32px 3%;
  min-height: 100vh;
  margin: 0px auto;
  box-sizing: border-box;
}

/* Apply max-width only when in carteras tab */
.portfolio-wrapper.carteras-tab {
  max-width: 1400px;
}

/* Beta Banner Styles */
.beta-banner {
  background: linear-gradient(135deg, #f39c12, #e67e22);
  border-radius: 8px;
  margin-bottom: 2rem;
  padding: 12px 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border-left: 4px solid #d35400;
}

.beta-banner-content {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.beta-badge {
  background-color: #d35400;
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  flex-shrink: 0;
}

.beta-text {
  color: white;
  font-size: 0.9rem;
  font-weight: 500;
  line-height: 1.4;
}

.beta-link {
  color: white;
  text-decoration: underline;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  transition: all 0.2s ease;
}

.beta-link:hover {
  color: #f1c40f;
  text-decoration: none;
}

.beta-link .material-icons-outlined {
  font-size: 16px;
  vertical-align: middle;
}

/* Main container styles */
.portfolio-manager-gcn {
  color: var(--color-text-primary);
  font-size: 0.95rem;
  margin-bottom: 20px;
}

/* Selected Portfolio Name Display */
.selected-portfolio-name {
  background-color: var(--color-surface);
  border: 1px solid var(--color-outline);
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;
}

.portfolio-label {
  color: var(--color-text-secondary);
  font-weight: 500;
}

.portfolio-name {
  color: var(--color-text-primary);
  font-weight: 600;
  background-color: var(--color-highlight);
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.85rem;
}

/* Portfolio Summary Header Styles */
.portfolio-summary-header {
  background-color: var(--color-surface);
  margin-bottom: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 4px var(--color-outline);
  border: 1px solid var(--color-outline);
  overflow: hidden;
}

.summary-header-toggle {
  padding: 15px 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: var(--color-surface);
  border-bottom: 1px solid var(--color-outline);
  transition: background-color 0.2s ease;
}

.summary-header-toggle:hover {
  background-color: var(--color-active-surface);
}

.summary-title {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 1rem;
  font-weight: 600;
  color: var(--color-text-primary);
}

.summary-title .material-icons-outlined {
  font-size: 24px;
  color: var(--color-highlight);
}

.summary-badge {
  background-color: var(--color-highlight);
  color: white;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
}

.toggle-icon {
  font-size: 24px;
  color: var(--color-text-secondary);
  transition: transform 0.3s ease;
}

.toggle-icon.expanded {
  transform: rotate(180deg);
}

.summary-content {
  padding: 15px 20px 15px 20px;
}

.portfolio-metrics-container {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.metrics-section {
  background-color: var(--color-background);
  border-radius: 6px;
  padding: 16px;
  border: 1px solid var(--color-outline);
}

.virtual-metrics {
  border-left: 4px solid #00add9;
}

.real-metrics {
  border-left: 4px solid #4CAF50;
}

.summary-metrics {
  border-left: 4px solid #FF9800;
}

.section-title {
  margin: 0 0 12px 0;
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--color-text-primary);
  display: flex;
  align-items: center;
  gap: 8px;
}

.section-title .material-icons-outlined {
  font-size: 20px;
  color: var(--color-highlight);
}

.portfolio-metrics {
  list-style: none;
  padding: 0;
  margin: 0;
  font-size: 0.9rem;
  color: var(--color-text-secondary);
}

.portfolio-metrics li {
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  line-height: 1.4;
}

.portfolio-metrics li:last-child {
  margin-bottom: 0;
}

.portfolio-metrics b {
  color: var(--color-text-primary);
  font-weight: 600;
  margin: 0 4px;
}

.portfolio-metrics .material-icons-outlined {
  font-size: 18px;
  margin-right: 6px;
  color: var(--color-highlight);
  vertical-align: middle;
}

.portfolio-metrics .separator {
  margin: 0 10px;
  color: var(--color-outline);
  font-weight: bold;
}

.loading-indicator-gcn.small, .error-message-gcn.small {
  font-size: 0.85em;
  padding: 8px 12px;
  text-align: left;
  margin-bottom: 0;
  border-radius: 4px;
}

.loading-indicator-gcn.small {
  color: var(--color-text-secondary);
  background-color: transparent;
}

.error-message-gcn.small {
  color: var(--color-error);
  background-color: var(--color-active-surface);
}

.portfolio-title {
  font-size: 2.25rem;
  font-weight: 700;
  line-height: 1.1;
  letter-spacing: -0.02em;
}

.portfolio-text {
  font-size: 1.05rem;
  font-weight: 400;
  line-height: 1.6;
  color: var(--color-text-secondary);
}

/* tabsGcn container */
.tabsGcn {
  margin-bottom: 2rem;
  background-color: transparent;
  padding: 0;
  border-radius: 0;
  border: none;
  border-bottom: 1px solid var(--color-outline);
}

/* Tab buttons */
.tab-button-gcn {
  background-color: transparent;
  color: var(--color-text-secondary);
  border: none;
  border-bottom: 2px solid transparent;
  border-radius: 0;
  padding: 12px 20px;
  font-size: 0.95rem;
  margin-right: 0;
  cursor: pointer;
  transition: all 0.2s ease;
  outline: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 500;
  position: relative;
}

.tab-button-gcn:hover:not([disabled]) {
  color: var(--color-text-primary);
  background-color: transparent;
}

.tab-button-gcn.active {
  color: var(--color-highlight);
  border-bottom-color: var(--color-highlight);
  background-color: transparent;
  font-weight: 600;
}

.tab-button-gcn[disabled] {
  opacity: 0.5;
  cursor: not-allowed;
  color: var(--color-text-inactive);
  background-color: transparent;
}

/* Tab content container */
.tab-content-gcn {
  background-color: transparent;
  border-radius: 0;
  min-height: 400px;
  transition: opacity 0.2s ease;
  box-shadow: none;
  border: none;
  overflow: visible;
  position: relative;
  margin-top: 1rem;
}

/* Desktop and Mobile responsive styles */
.desktop-tabsGcn {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.mobile-tabsGcn {
  display: none;
  width: 100%;
  margin-bottom: 1rem;
}

.tab-select {
  width: 100%;
  padding: 12px 16px;
  border-radius: 8px;
  background-color: var(--color-surface);
  border: 1px solid var(--color-outline);
  color: var(--color-text-primary);
  font-size: 1rem;
  cursor: pointer;
  outline: none;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
  appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 12px center;
  background-size: 16px;
  padding-right: 40px;
}

.tab-select:focus {
  border-color: var(--color-highlight);
  box-shadow: 0 0 0 3px rgba(var(--color-highlight-rgb), 0.25);
}

.tab-select:hover {
  border-color: var(--color-highlight);
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

/* Tab transition animation */
.tab-fade-enter-active, .tab-fade-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.tab-fade-enter-from, .tab-fade-leave-to {
  opacity: 0;
  transform: translateY(10px);
}

.tab-fade-enter-to, .tab-fade-leave-from {
  opacity: 1;
  transform: translateY(0);
}



/* Media query for mobile view */
@media (max-width: 768px) {
  .desktop-tabsGcn {
    display: none;
  }
  
  .mobile-tabsGcn {
    display: block;
  }
  
  .portfolio-manager-gcn {
    padding: 0 8px;
  }
  
  .portfolio-manager-gcn h1 {
    font-size: 1.3rem;
    margin-top: 0.5rem;
  }

  .tab-content-gcn {
    border-radius: 6px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }

  /* Beta Banner Mobile Styles */
  .beta-banner {
    margin: 0 -3% 2rem -3%;
    border-radius: 0;
    padding: 16px 5%;
  }

  .beta-banner-content {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .beta-text {
    font-size: 0.85rem;
  }

  /* Portfolio Metrics Mobile Styles */
  .portfolio-metrics-container {
    gap: 15px;
  }

  .metrics-section {
    padding: 12px;
  }

  .section-title {
    font-size: 0.9rem;
    margin-bottom: 10px;
  }

  .portfolio-metrics li {
    font-size: 0.85rem;
    margin-bottom: 6px;
  }

  /* Portfolio Summary Mobile Styles */
  .summary-header-toggle {
    padding: 12px 16px;
  }

  .summary-title {
    font-size: 0.9rem;
    gap: 8px;
  }

  .summary-title .material-icons-outlined {
    font-size: 20px;
  }

  .summary-badge {
    font-size: 0.7rem;
    padding: 1px 6px;
  }

  .toggle-icon {
    font-size: 20px;
  }

  .summary-content {
    padding: 12px 16px 12px 16px;
  }
}
</style>